import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Activity, ImpactResult } from '../types';
import { ImpactAnalysis } from './Layout';
import { DemoSignInModal } from './DemoSignInModal';
import { useFlowClient } from '../hooks/useFlowClient';
import { useDebounce } from '../hooks/useDebounce';
import { useDemoGuard } from '../hooks/useDemoGuard';
import { trackEvent } from '../utils/clarity';
import { getRiskColors, getFloatRisk, getFloatLabel } from '../utils/riskUtils';
import { formatDate, formatDateInput } from '../utils/dateUtils';
import { useApp } from '../context/AppContext';

export interface ExpandedRowProps {
  activity: Activity;
  tab?: 'week' | 'all';
  colSpan: number;
  userEmail: string;
  projectId: string;
  activityNames: Record<string, string>;
  onSubmitSuccess: (changes: Partial<Activity>, originals: Partial<Activity>) => void;
  onSubmitError?: (message: string) => void;
  onSubmitStart?: () => void;
  onCancel: () => void;
}

interface FieldChange {
  oldValue: string | number;
  newValue: string | number;
}

interface EditFields {
  planned_start: string;
  planned_finish: string;
  actual_start: string;
  actual_finish: string;
  forecast_start: string;
  forecast_finish: string;
  pct_complete: number;
  status: string;
}

type EditableField = 'actual_start' | 'actual_finish' | 'forecast_start' | 'forecast_finish' | 'pct_complete';

const IMPACT_FIELDS = new Set(['actual_start', 'actual_finish', 'forecast_start', 'forecast_finish']);

function computeAutoStatus(actualStart: string, actualFinish: string): string {
  if (actualFinish) return 'Complete';
  if (actualStart) return 'In Progress';
  return 'Not Started';
}

function statusBadgeClass(s: string): string {
  if (s === 'Complete' || s === 'Completed') return 'bg-tint text-accent';
  if (s === 'In Progress') return 'bg-watch-bg text-watch-text';
  return 'bg-gray-100 text-muted dark:bg-gray-700 dark:text-gray-300';
}

const ExpandedRow: React.FC<ExpandedRowProps> = ({
  activity,
  tab,
  colSpan,
  userEmail,
  projectId,
  activityNames,
  onSubmitSuccess,
  onSubmitError,
  onSubmitStart,
  onCancel,
}) => {
  const flowClient = useFlowClient();
  const { projects, setProjects } = useApp();
  const { isDemoMode, gateOpen, gateFeature, closeGate, guard } = useDemoGuard();
  const floatRisk = getFloatRisk(activity.total_float);
  const floatColors = getRiskColors(floatRisk);

  // Per-project role, not a global user role — the same person can be PM on one
  // project and Viewer on another, so this must be looked up by projectId every time.
  const project = projects.find(p => p.id === projectId);
  const isViewer = project?.role_on_project === 'Viewer';

  const buildInitFields = useCallback((): EditFields => ({
    planned_start: formatDateInput(activity.planned_start),
    planned_finish: formatDateInput(activity.planned_finish),
    actual_start: formatDateInput(activity.actual_start),
    actual_finish: formatDateInput(activity.actual_finish),
    forecast_start: formatDateInput(activity.forecast_start),
    forecast_finish: formatDateInput(activity.forecast_finish),
    pct_complete: activity.pct_complete ?? 0,
    status: computeAutoStatus(
      formatDateInput(activity.actual_start),
      formatDateInput(activity.actual_finish),
    ),
  }), [activity]);

  const [editFields, setEditFields] = useState<EditFields>(buildInitFields);
  const [pendingChanges, setPendingChanges] = useState<Record<string, FieldChange>>({});
  const [impactTrigger, setImpactTrigger] = useState<{ field: string; oldValue: string | number; newValue: string | number } | null>(null);
  const [impactResult, setImpactResult] = useState<ImpactResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [futureDateWarning, setFutureDateWarning] = useState<{ date: string; field: 'actual_start' | 'actual_finish' } | null>(null);

  const debouncedImpact = useDebounce(impactTrigger, 2000);
  const prevDebounced = useRef<typeof impactTrigger>(null);

  useEffect(() => {
    if (!debouncedImpact) return;
    if (debouncedImpact === prevDebounced.current) return;
    if (String(debouncedImpact.oldValue) === String(debouncedImpact.newValue)) return;
    prevDebounced.current = debouncedImpact;
    setChecking(true);
    setImpactResult(null);
    flowClient
      .impact(userEmail, projectId, activity.activity_id, debouncedImpact.field, debouncedImpact.oldValue, debouncedImpact.newValue)
      .then((result) => {
        setImpactResult(result);
        if (isDemoMode) trackEvent('what_if_run');
      })
      .catch(() => setSubmitError('Impact analysis failed. You can still submit.'))
      .finally(() => setChecking(false));
  }, [debouncedImpact, userEmail, projectId, activity.activity_id, flowClient]);

  const handleFieldChange = (field: EditableField, value: string | number) => {
    if ((field === 'actual_start' || field === 'actual_finish') && typeof value === 'string' && value) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const [y, m, d] = value.split('-').map(Number);
      const sel = new Date(y, m - 1, d);
      if (sel > today) {
        setFutureDateWarning({ date: value, field });
        return;
      }
    }
    const origVal = (activity as unknown as Record<string, unknown>)[field];
    const newEdit = { ...editFields, [field]: value };
    const newPending: Record<string, FieldChange> = {
      ...pendingChanges,
      [field]: { oldValue: String(origVal ?? ''), newValue: value },
    };
    if (field === 'actual_start' || field === 'actual_finish') {
      const cs = computeAutoStatus(String(newEdit.actual_start), String(newEdit.actual_finish));
      newEdit.status = cs;
      newPending['activity_status'] = { oldValue: String(activity.status ?? ''), newValue: cs };
    }
    setEditFields(newEdit);
    setPendingChanges(newPending);
    if (IMPACT_FIELDS.has(field)) {
      setImpactTrigger({ field, oldValue: String(origVal ?? ''), newValue: value });
    }
    setImpactResult(null);
    setSubmitError(null);
    setFutureDateWarning(null);
  };

  const handleUseForecast = () => {
    if (!futureDateWarning) return;
    const { date, field } = futureDateWarning;
    setFutureDateWarning(null);
    if (field === 'actual_finish') {
      const orig = (activity as unknown as Record<string, unknown>)['forecast_finish'];
      const cs = computeAutoStatus(editFields.actual_start, '');
      setEditFields(prev => ({ ...prev, actual_finish: '', forecast_finish: date, status: cs }));
      setPendingChanges(prev => {
        const next = { ...prev };
        delete next['actual_finish'];
        next['forecast_finish'] = { oldValue: String(orig ?? ''), newValue: date };
        next['activity_status'] = { oldValue: String(activity.status ?? ''), newValue: cs };
        return next;
      });
      setImpactTrigger({ field: 'forecast_finish', oldValue: String(orig ?? ''), newValue: date });
    } else {
      const orig = (activity as unknown as Record<string, unknown>)['forecast_start'];
      setEditFields(prev => ({ ...prev, actual_start: '', forecast_start: date }));
      setPendingChanges(prev => {
        const next = { ...prev };
        delete next['actual_start'];
        next['forecast_start'] = { oldValue: String(orig ?? ''), newValue: date };
        return next;
      });
      setImpactTrigger({ field: 'forecast_start', oldValue: String(orig ?? ''), newValue: date });
    }
    setImpactResult(null);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    const entries = Object.entries(pendingChanges);
    if (!entries.length) return;
    setSubmitting(true);
    setSubmitError(null);
    onSubmitStart?.();
    try {
      const result = await flowClient.approveBatch(
        userEmail, projectId, activity.activity_id,
        entries.map(([field, { oldValue, newValue }]) => ({ field_name: field, new_value: newValue, old_value: oldValue })),
        impactResult?.risk_level ?? 'Low',
        impactResult?.summary ?? 'Manual update',
      );
      // Build originals for the parent
      const originals: Partial<Activity> = {};
      for (const [field, { oldValue }] of entries) {
        (originals as Record<string, string | number>)[field] = oldValue;
      }
      // Prefer server-computed CPM values over locally-known field changes
      if (result.updatedActivity) {
        const ua = result.updatedActivity;
        const activityPatch: Partial<Activity> = {
          planned_start: ua.start_date,
          planned_finish: ua.end_date,
          actual_start: ua.act_start_date ?? null,
          actual_finish: ua.act_end_date ?? null,
          forecast_start: ua.forecast_start_date ?? null,
          forecast_finish: ua.forecast_end_date ?? null,
          start_date: ua.Start_Date ?? null,
          finish_date: ua.Finish_Date ?? null,
          total_float: ua.float_days ?? 0,
          pct_complete: ua.complete_pct ?? 0,
          activity_status: ua.activity_status,
        };
        onSubmitSuccess(activityPatch, originals);
      } else {
        const changes: Partial<Activity> = {};
        for (const [field, { newValue }] of entries) {
          (changes as Record<string, string | number>)[field] = newValue;
        }
        onSubmitSuccess(changes, originals);
      }

      // Refresh project-level data so Dashboard and Portfolio immediately reflect
      // the new cpm_completion_date, distressed_mode, etc. written by runAndSaveCPM.
      try {
        const refreshed = await flowClient.projects(userEmail);
        if (Array.isArray(refreshed.projects)) setProjects(refreshed.projects);
      } catch {
        // non-fatal — the activity save already succeeded
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submit failed. Please try again.';
      setSubmitError(message);
      setSubmitting(false);
      onSubmitError?.(message);
    }
  };

  const changeCount = Object.keys(pendingChanges).length;
  const isHighRisk = impactResult?.risk_level === 'High';

  // Actual Finish requires an Actual Start — either already stored on the activity, or
  // included in this same edit. editFields.actual_start already reflects whichever of
  // those applies (unchanged fields show the stored value; edited fields show the new one),
  // so no separate "existing vs. payload" branching is needed here — mirrors the backend
  // check in validation.ts, which does need that distinction since it only sees the diff.
  const isSettingActualFinish = 'actual_finish' in pendingChanges && pendingChanges.actual_finish.newValue !== '';
  const missingActualStartForFinish = isSettingActualFinish && !editFields.actual_start;

  const canSubmit = !isViewer && changeCount > 0 && !checking && !submitting && !missingActualStartForFinish;

  const readOnlyDate = (label: string, val: string) => (
    <div>
      <label className="block text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide mb-1">
        {label}
      </label>
      <div className="w-full text-sm border border-gray-100 dark:border-gray-700 rounded-[8px] px-2.5 py-2 bg-gray-50 dark:bg-gray-800 text-muted dark:text-gray-400 min-h-[40px] flex items-center">
        {formatDate(val) === 'Not recorded' ? '—' : formatDate(val)}
      </div>
    </div>
  );

  const actualDateInput = (field: 'actual_start' | 'actual_finish', label: string) => {
    const isWarn = futureDateWarning?.field === field;
    return (
      <div>
        <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isWarn ? 'text-crit-text' : 'text-muted dark:text-gray-400'}`}>
          {label}
        </label>
        <input
          type="date"
          value={String(editFields[field])}
          onChange={e => handleFieldChange(field, e.target.value)}
          className={`w-full text-sm rounded-[8px] px-2.5 py-2 focus:outline-none min-h-[40px] tap transition-colors ${
            isWarn
              ? 'border-2 border-crit-text ring-2 ring-crit-text/20 bg-crit-bg'
              : 'bg-white dark:bg-gray-700 text-text dark:text-gray-100 border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-accent'
          }`}
        />
        {isWarn && (
          <div className="mt-1.5 bg-crit-bg border border-crit-text rounded-[6px] p-2.5">
            <p className="text-xs text-crit-text font-semibold mb-1">Actual dates cannot be in the future.</p>
            <p className="text-xs text-crit-text mb-2">Use Forecast Start/Finish for planned future dates.</p>
            <div className="flex gap-2">
              <button
                onClick={handleUseForecast}
                className="flex-1 bg-accent text-white text-xs font-semibold py-1.5 rounded-[6px] tap"
              >
                Use as {field === 'actual_finish' ? 'Forecast Finish' : 'Forecast Start'}
              </button>
              <button
                onClick={() => setFutureDateWarning(null)}
                className="flex-1 border border-crit-text text-crit-text text-xs font-semibold py-1.5 rounded-[6px] tap"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const forecastDateInput = (field: 'forecast_start' | 'forecast_finish', label: string, hint: string) => {
    const disabledByActual =
      (field === 'forecast_start' && !!editFields.actual_start) ||
      (field === 'forecast_finish' && !!editFields.actual_finish);
    return (
      <div className={disabledByActual ? 'opacity-40 pointer-events-none' : ''}>
        <label className="block text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide mb-1">
          {label}
        </label>
        <input
          type="date"
          value={String(editFields[field])}
          onChange={e => handleFieldChange(field as EditableField, e.target.value)}
          disabled={disabledByActual}
          className="w-full text-sm rounded-[8px] px-2.5 py-2 bg-white dark:bg-gray-700 text-text dark:text-gray-100 border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-accent min-h-[40px] tap"
        />
        <p className="text-[11px] text-muted dark:text-gray-500 mt-0.5">
          {disabledByActual
            ? field === 'forecast_start'
              ? 'Not needed — actual start is recorded'
              : 'Not needed — actual finish is recorded'
            : hint}
        </p>
      </div>
    );
  };

  const resources = activity.resource
    ? activity.resource.split(',').map(r => r.trim()).filter(Boolean)
    : [];

  return (
    <tr>
      <td colSpan={colSpan} className="p-0 border-b-2 border-accent/30" onClick={e => e.stopPropagation()}>
        <div className="sticky left-0 max-w-[100vw] bg-white dark:bg-gray-900 border-l-4 border-accent px-4 sm:px-5 py-4 space-y-4">

          {/* Field grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Row 1: Start/Finish dates (read-only) + Actual dates (editable for PM, read-only for Viewer) */}
            {readOnlyDate('Start', formatDateInput(activity.start_date))}
            {readOnlyDate('Finish', formatDateInput(activity.finish_date))}
            {isViewer ? readOnlyDate('Actual Start', editFields.actual_start) : actualDateInput('actual_start', 'Actual Start')}
            {isViewer ? readOnlyDate('Actual Finish', editFields.actual_finish) : actualDateInput('actual_finish', 'Actual Finish')}

            {/* Row 2: Forecast dates + % complete slider */}
            {isViewer ? readOnlyDate('Forecast Start', editFields.forecast_start) : forecastDateInput('forecast_start', 'Forecast Start', 'Enter expected start if not confirmed')}
            {isViewer ? readOnlyDate('Forecast Finish', editFields.forecast_finish) : forecastDateInput('forecast_finish', 'Forecast Finish', 'Enter expected finish if not confirmed')}

          </div>

          {isViewer && (
            <p className="text-xs text-muted dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-[8px]">
              View only — you have Viewer access to this project.
            </p>
          )}

          {missingActualStartForFinish && (
            <p className="text-xs text-crit-text bg-crit-bg px-3 py-2 rounded-[8px]">
              Actual Start is required before entering Actual Finish.
            </p>
          )}

          {/* Status + CPM */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide">Status</span>
              <span className={`text-sm font-semibold px-3 py-1 rounded-full ${statusBadgeClass(editFields.status)}`}>
                {editFields.status}
              </span>
              <span className="text-xs text-muted dark:text-gray-400">(auto)</span>
            </div>

            {/* Float / CPM */}
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted dark:text-gray-500">Float · CPM</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${floatColors.bgClass} ${floatColors.textClass}`}>
                {getFloatLabel(activity.total_float)}
              </span>
              {activity.is_critical === true && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-crit-bg text-crit-text">
                  CRITICAL PATH
                </span>
              )}
            </div>
          </div>

          {/* Trade packages */}
          {resources.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted dark:text-gray-500 mb-1.5">
                Trade Packages
              </p>
              <div className="flex flex-wrap gap-1.5">
                {resources.map((r, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-muted dark:text-gray-300">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Impact analysis zone */}
          {!isViewer && !checking && !impactResult && changeCount === 0 && (
            <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-[8px] p-4 text-center">
              <p className="text-sm text-muted dark:text-gray-400">
                Change any field to see CPM impact before submitting.
              </p>
            </div>
          )}
          {checking && (
            <div className="flex items-center gap-2 text-sm text-muted dark:text-gray-400 py-2">
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span>Running CPM analysis...</span>
            </div>
          )}
          {impactResult && !checking && (
            <ImpactAnalysis result={impactResult} activityNames={activityNames} />
          )}

          {/* Submit error */}
          {submitError && (
            <p className="text-xs text-crit-text bg-crit-bg px-3 py-2 rounded-[8px]">{submitError}</p>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onCancel}
              className="flex-1 border border-gray-300 dark:border-gray-600 text-muted dark:text-gray-400 font-semibold py-2.5 rounded-[8px] text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors min-h-[40px] tap"
            >
              Cancel
            </button>
            {canSubmit && (
              <button
                onClick={() => guard(handleSubmit, 'submit schedule updates')}
                disabled={submitting}
                className={`flex-[2] font-semibold py-2.5 rounded-[8px] text-sm disabled:opacity-60 min-h-[40px] tap transition-colors ${
                  isHighRisk
                    ? 'bg-crit-text hover:bg-crit-text text-white'
                    : 'bg-accent hover:bg-link text-white'
                }`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : isHighRisk && impactResult
                  ? `Submit anyway (+${impactResult.days_added_to_completion}d)`
                  : changeCount > 1
                  ? `Save ${changeCount} changes`
                  : 'Submit update'}
              </button>
            )}
          </div>
        </div>
        <DemoSignInModal open={gateOpen} onClose={closeGate} feature={gateFeature} />
      </td>
    </tr>
  );
};

export default ExpandedRow;
